// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { RefObject } from "react";
import { useEffect } from "react";

function createRipple(el: HTMLElement, e: PointerEvent) {
  const rect = el.getBoundingClientRect();
  const ripple = document.createElement("span");
  const diameter = Math.max(rect.width, rect.height) * 2;
  ripple.style.width = ripple.style.height = `${diameter}px`;
  ripple.style.left = `${e.clientX - rect.left - diameter / 2}px`;
  ripple.style.top = `${e.clientY - rect.top - diameter / 2}px`;
  ripple.className = "btn-ripple";
  el.appendChild(ripple);

  function release() {
    ripple.classList.add("btn-ripple-out");
    ripple.addEventListener("animationend", (ev) => {
      if (ev.animationName === "btn-ripple-out") ripple.remove();
    });
    window.removeEventListener("pointerup", release);
    window.removeEventListener("pointercancel", release);
  }
  window.addEventListener("pointerup", release);
  window.addEventListener("pointercancel", release);
}

export function useRipple(ref: RefObject<HTMLElement | null>, enabled = true) {
  useEffect(
    function attachRippleListener() {
      const el = ref.current;
      if (!el || !enabled) return;

      function onPointerDown(e: PointerEvent) {
        if (e.button !== 0) return;
        if (e.target instanceof HTMLElement && e.target.closest("[data-no-ripple]")) return;
        createRipple(el!, e);
      }

      el.addEventListener("pointerdown", onPointerDown);
      return () => el.removeEventListener("pointerdown", onPointerDown);
    },
    [ref, enabled],
  );
}

/**
 * Delegated ripple: attaches a single listener on a container and creates
 * ripples on the closest `[data-ripple]` descendant that was clicked.
 * Accepts an element directly (not a ref) so it works with lazily-mounted content.
 */
export function useRippleDelegated(container: HTMLElement | null) {
  useEffect(
    function attachDelegatedRippleListener() {
      if (!container) return;

      function onPointerDown(e: PointerEvent) {
        if (e.button !== 0) return;
        const target = (e.target as HTMLElement).closest?.("[data-ripple]") as HTMLElement | null;
        if (!target || !container!.contains(target)) return;
        createRipple(target, e);
      }

      container.addEventListener("pointerdown", onPointerDown);
      return () => container.removeEventListener("pointerdown", onPointerDown);
    },
    [container],
  );
}
